import { defineComponent as _defineComponent } from 'vue'
import { createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, mergeProps as _mergeProps, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "d-flex px-4"
}
const _hoisted_2 = { class: "table-col col-date p-1" }
const _hoisted_3 = { class: "table-col col-amount p-1" }
const _hoisted_4 = { class: "table-col col-status p-1" }
const _hoisted_5 = { class: "d-flex px-4" }
const _hoisted_6 = { class: "table-col col-date p-1" }
const _hoisted_7 = { class: "table-col col-amount p-1" }
const _hoisted_8 = {
  key: 0,
  class: "text-refund"
}
const _hoisted_9 = { class: "table-col col-status p-1" }
const _hoisted_10 = { class: "table-col col-action p-1" }

import { ref, computed } from 'vue'
import { StripeInvoice, StripeUpcomingInvoice } from '../../../api/v2/models/subscription'


export default /*@__PURE__*/_defineComponent({
  __name: 'StripeInvoice',
  props: {
    stripeInvoice: {},
    showMembershipFee: { type: Boolean, default: false },
    membershipFee: {},
    membershipFeePaidAt: {},
    membershipFeeChargeAt: {},
    membershipFeeChargeFailed: { type: Boolean },
    membershipFeeChargeFailedMessage: {},
    isLastInvoice: { type: Boolean, default: false },
    needMembershipFee: { type: Boolean, default: false },
    stripeUpcomingInvoice: {},
    membershipStatus: {},
    downloadingReceiptId: { default: '' }
  },
  emits: ["download-receipt"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const retryDialog = ref(false)

const formatDate = (input: string | number | Date): string => {
  let timestamp: number
  if (input instanceof Date) {
    timestamp = input.getTime()
  } else if (typeof input === 'string') {
    timestamp = new Date(input).getTime()
  } else {
    timestamp = input * 1000
  }
  const date = new Date(timestamp)
  return date.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '/')
}

const membershipFeeDate = computed(() => {
  // 初回の請求に入会金がまとめて入ると membershipFeePaidAt が空のまま残る。
  // そのとき次の請求の期間の始まりを出すと、2回目の請求以降は入会金の日付がずれていくので、
  // 入会金を払った請求書（入会金の行はいちばん古い請求書に付く）の日付を先に見る
  const invoice = props.stripeInvoice
  const hasFeeLine = invoice.lines.data.some((line) => (line.amount || 0) > 0 && line.description?.includes('Membership Fee'))
  if (hasFeeLine && invoice.status === 'paid') {
    return formatDate(invoice.paidAt || invoice.created)
  }
  if (props.membershipFeeChargeAt) {
    return formatDate(props.membershipFeeChargeAt)
  }
  if (props.membershipFeePaidAt) {
    return formatDate(props.membershipFeePaidAt)
  }
  if (props.stripeUpcomingInvoice) {
    return formatDate(props.stripeUpcomingInvoice.periodStart)
  }

  return formatDate(props.stripeInvoice.created)
})

const getMembershipFeeStatusColor = computed(() => {
  if (props.membershipStatus === 'paid') return 'success'
  if (props.membershipFeePaidAt) return 'success'
  if (props.membershipFeeChargeFailed) return 'error'
  return 'primary'
})

const getMembershipFeeStatusText = computed(() => {
  if (props.membershipStatus === 'paid') return '支払い済み'
  if (props.membershipFeePaidAt) return '支払い済み'
  if (props.membershipFeeChargeFailed) return '支払い失敗'
  return '支払い待ち'
})

const getMembershipFeeTooltip = computed(() => {
  if (props.membershipStatus === 'paid') return '入会金の支払いが完了しました。'
  if (props.membershipFeePaidAt) return '入会金の支払いが完了しました。'
  if (props.membershipFeeChargeFailed) return props.membershipFeeChargeFailedMessage || ''
  return '入会金の支払いが必要です。'
})

const getAmount = (invoice: any) => {
  let amount = invoice.amountPaid || invoice.amountDue
  let membershipFee = 0
  if (invoice.lines.data.length > 1) {
    membershipFee = invoice.lines.data.find((line: any) => line.description?.includes('Membership Fee'))?.amount || 0
  }

  if (membershipFee > 0) {
    return amount - membershipFee
  }

  return amount
}

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'draft':
      return 'info'
    case 'open':
      return 'warning'
    case 'paid':
      return 'success'
    case 'void':
      return 'grey'
    case 'uncollectible':
      return 'error'
    default:
      return 'grey'
  }
}

const getStatusText = (status: string): string => {
  switch (status) {
    case 'draft':
      return '下書き'
    case 'open':
      return '未支払い'
    case 'paid':
      return '支払い済み'
    case 'void':
      return 'キャンセル'
    case 'uncollectible':
      return '回収不能'
    default:
      return status
  }
}

const getStatusTooltip = (status: string): string => {
  switch (status) {
    case 'draft':
      return '請求書はまだ使用する準備ができていません。すべての請求書は下書き状態で開始されます。'
    case 'open':
      return '請求書は確定済みで支払い待ちです。'
    case 'paid':
      return 'この請求書は支払い済みです。これ以上のアクションは必要ありません。'
    case 'void':
      return 'この請求書はキャンセルされました。これ以上のアクションは必要ありません。'
    case 'uncollectible':
      return '顧客が請求書を支払う可能性が低いです。通常、会計処理で不良債権として扱います。'
    default:
      return ''
  }
}

const openNewTab = (url: string): void => {
  window.open(url, '_blank')
}

const isMembershipFeeInvoice = computed(() => {
  return props.isLastInvoice && props.needMembershipFee
})

const shouldShowMembershipFee = computed(() => {
  if (!props.showMembershipFee || !isMembershipFeeInvoice.value) {
    return false
  }

  const isCanceledWithPending = props.stripeInvoice.status === 'canceled' && getMembershipFeeStatusText.value === '支払い待ち'
  const noUpcomingAndNotPaid = props.stripeUpcomingInvoice === null && props.membershipStatus !== 'paid'

  return !(isCanceledWithPending || noUpcomingAndNotPaid)
})

const shouldShowMembershipFeeWithDivider = computed(() => {
  return shouldShowMembershipFee.value && getMembershipFeeStatusText.value === '支払い待ち'
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_btn = _resolveComponent("v-btn")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createCommentVNode(" Membership Fee "),
    (shouldShowMembershipFee.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, _toDisplayString(membershipFeeDate.value), 1 /* TEXT */),
          _createElementVNode("div", _hoisted_3, _toDisplayString(__props.membershipFee.toLocaleString()) + "円 （入会金）", 1 /* TEXT */),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_component_v_tooltip, { location: "bottom" }, {
              activator: _withCtx(({ props }) => [
                _createVNode(_component_v_chip, _mergeProps({
                  class: "me-2 text-white",
                  color: getMembershipFeeStatusColor.value,
                  size: "small",
                  label: ""
                }, props), {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(getMembershipFeeStatusText.value) + " ", 1 /* TEXT */),
                    _createVNode(_component_v_icon, {
                      size: "small",
                      class: "ml-1"
                    }, {
                      default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                        _createTextVNode("mdi-information", -1 /* CACHED */)
                      ]))]),
                      _: 1 /* STABLE */
                    })
                  ]),
                  _: 1 /* STABLE */
                }, 16 /* FULL_PROPS */, ["color"])
              ]),
              default: _withCtx(() => [
                _createElementVNode("span", null, _toDisplayString(getMembershipFeeTooltip.value), 1 /* TEXT */)
              ]),
              _: 1 /* STABLE */
            })
          ]),
          _cache[3] || (_cache[3] = _createElementVNode("div", { class: "table-col col-action p-1" }, null, -1 /* CACHED */))
        ]))
      : _createCommentVNode("v-if", true),
    (shouldShowMembershipFeeWithDivider.value)
      ? (_openBlock(), _createBlock(_component_v_divider, {
          key: 1,
          class: "mt-2"
        }))
      : _createCommentVNode("v-if", true),
    _createCommentVNode(" Regular Invoice "),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, _toDisplayString(formatDate(__props.stripeInvoice.created)), 1 /* TEXT */),
      _createElementVNode("div", _hoisted_7, [
        _createTextVNode(_toDisplayString(getAmount(__props.stripeInvoice).toLocaleString()) + "円 ", 1 /* TEXT */),
        (__props.stripeInvoice.amountRefunded)
          ? (_openBlock(), _createElementBlock("span", _hoisted_8, "（返金済み " + _toDisplayString(__props.stripeInvoice.amountRefunded.toLocaleString()) + "円）", 1 /* TEXT */))
          : _createCommentVNode("v-if", true)
      ]),
      _createElementVNode("div", _hoisted_9, [
        _createVNode(_component_v_tooltip, { location: "bottom" }, {
          activator: _withCtx(({ props }) => [
            _createVNode(_component_v_chip, _mergeProps({
              class: "me-2 text-white",
              color: getStatusColor(__props.stripeInvoice.status),
              size: "small",
              label: ""
            }, props), {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(getStatusText(__props.stripeInvoice.status)) + " ", 1 /* TEXT */),
                _createVNode(_component_v_icon, {
                  size: "small",
                  class: "ml-1"
                }, {
                  default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                    _createTextVNode("mdi-information", -1 /* CACHED */)
                  ]))]),
                  _: 1 /* STABLE */
                })
              ]),
              _: 1 /* STABLE */
            }, 16 /* FULL_PROPS */, ["color"])
          ]),
          default: _withCtx(() => [
            _createElementVNode("span", null, _toDisplayString(getStatusTooltip(__props.stripeInvoice.status)), 1 /* TEXT */)
          ]),
          _: 1 /* STABLE */
        })
      ]),
      _createElementVNode("div", _hoisted_10, [
        _withDirectives(_createVNode(_component_v_btn, {
          color: "warning",
          dark: "",
          size: "small",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (openNewTab(__props.stripeInvoice.hostedInvoiceUrl)))
        }, {
          default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
            _createTextVNode(" 支払いを確定する ", -1 /* CACHED */)
          ]))]),
          _: 1 /* STABLE */
        }, 512 /* NEED_PATCH */), [
          [_vShow, __props.stripeInvoice.status === 'open']
        ]),
        _withDirectives(_createVNode(_component_v_btn, {
          color: "primary",
          outlined: "",
          small: "",
          loading: __props.downloadingReceiptId === __props.stripeInvoice.id,
          disabled: __props.downloadingReceiptId === __props.stripeInvoice.id,
          onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('download-receipt', __props.stripeInvoice.id)))
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_icon, {
              small: "",
              class: "mr-1"
            }, {
              default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                _createTextVNode("mdi-file-pdf-box", -1 /* CACHED */)
              ]))]),
              _: 1 /* STABLE */
            }),
            _cache[7] || (_cache[7] = _createTextVNode(" 領収書 ", -1 /* CACHED */))
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["loading", "disabled"]), [
          [_vShow, __props.stripeInvoice.status === 'paid']
        ])
      ])
    ])
  ]))
}
}

})